import { useCallback, useContext, useEffect, useState } from 'react';
import _ from 'lodash';
import useAggregateMetricsGroupByInput from './useAggregateMetricsGroupByInput';
import aggregateMetricsGroupByInput from './aggregateMetricsGroupByInput';
import GqlClientContext from '../../../contexts/GqlClientContext';
import formatDateLabel from '../../V5Gadget/formatDateLabel';
import relativeDateRangeTypeCheckers from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/FiscalPeriodsProvider/relativeDateRangeTypeCheckers';

// When grouping by a date field, the backend returns each term's group value as
// the raw bucket start date. Convert it to a human-friendly label for display
// (e.g. "W/E 08/05") while keeping the raw date around for chronological
// sorting and drill-downs.
const toDateGroupedTerms = ({
  terms,
  groupByField,
  interval,
}: {
  terms: { [column: string]: string | number | undefined }[];
  groupByField: string;
  interval: FleetOps.Interval | FiscalInterval;
}) =>
  terms.map((term) => {
    const rawDate = term[groupByField];
    const label = relativeDateRangeTypeCheckers.isCalendarWithAutoInterval(
      interval,
    )
      ? formatDateLabel(String(rawDate), interval, true)
      : String(rawDate);
    return {
      ...term,
      'raw-date': rawDate,
      date: rawDate,
      [groupByField]: label,
    };
  });

const useRankingMatrixData = (
  gadget: VisualisationDefinitions.RankingMatrix,
) => {
  const { client } = useContext(GqlClientContext);
  const input = useAggregateMetricsGroupByInput(gadget);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [terms, setTerms] = useState<
    { [column: string]: string | number | undefined }[] | undefined
  >(undefined);
  const [totals, setTotals] = useState<
    { [column: string]: string | number | undefined }[] | undefined
  >(undefined);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const executeQuery = useCallback(
    _.debounce((args: Queries.AggregateMetricsGroupByInput) => {
      aggregateMetricsGroupByInput({ client, input: args }).then((response) => {
        const terms = gadget.interval
          ? toDateGroupedTerms({
              terms: response.terms,
              groupByField: gadget.groupByField,
              interval: gadget.interval,
            })
          : response.terms;
        setTerms(terms);
        const totalsWithCount = {
          [gadget.groupByField]: terms.length,
          ...response.totals,
        };
        setTotals([totalsWithCount]);
        setIsLoading(false);
      });
    }, 500),
    [gadget.groupByField, gadget.interval],
  );

  useEffect(() => {
    setIsLoading(true);
    setTotals(undefined);
    setTerms(undefined);
    if (!input) {
      return;
    }

    executeQuery(input);
  }, [client, executeQuery, input]);

  return {
    isLoading,
    terms,
    totals,
  };
};

export default useRankingMatrixData;
